import { VStack, Box, HStack, Button, Text } from "@chakra-ui/react";
import { useCallback } from "react";
import { PriceDisplay } from "../../../cards/components/PriceDisplay";
import { TimerBadge } from "../../../cards/components/TimerBadge";
import { AllReleasesOwnedBadge } from "../../../cards/components/AllReleasesOwnedBadge";
import { Bundle, Price } from "../../../../types";
import { t } from "i18next";
import { useAccountData } from "../../../../hooks/useAccountData";
import { useBundlePurchaseFunnel } from "../../../../hooks/useBundlePurchaseFunnel";
import { FunnelConfigTypes } from "../../../funnel/types";
import { ClaimWithGoldButton } from "../../../cards/ClaimWithGoldButton";

export interface BundleDetailsPricingInformationProps {
  bundle: Bundle;
  priceToUse: Price;
  hasDiscount: boolean;
  discountPercentage: number;
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
}

export function BundleDetailsPricingInformation({
  bundle,
  priceToUse,
  hasDiscount,
  discountPercentage,
  setFunnelConfig,
}: BundleDetailsPricingInformationProps) {
  const accountData = useAccountData();
  const user = accountData?.user;
  const isLoggedIn = !!user;

  // If user owns all items, show owned status instead of price/buy button
  if (bundle.user_owns_all_items) {
    return (
      <Box zIndex={1}>
        <AllReleasesOwnedBadge size="md" />
      </Box>
    );
  }

  // Claimable Gold-Monthly bundle for a Gold member who hasn't claimed yet —
  // show $0.00 + strikethrough retail and the shared Claim CTA, mirroring
  // how releases render the same state on details / hero / feature cards.
  const isClaimable =
    !!user?.gold &&
    !!bundle.gold_monthly_title_id &&
    bundle.gold_monthly_claim_status === "unclaimed";

  if (isClaimable) {
    return (
      <VStack spacing="12px" alignItems="flex-start" zIndex={1}>
        <HStack spacing="8px" alignItems="baseline">
          <Text fontSize="20px" fontWeight={700} color="success.500">
            $0.00
          </Text>
          {bundle.price?.display_price && (
            <Text
              fontSize="14px"
              color="white"
              textDecoration="line-through"
              opacity={0.7}
            >
              {bundle.price.display_price}
            </Text>
          )}
        </HStack>
        <HStack spacing="12px" width="100%" zIndex={1}>
          <ClaimWithGoldButton />
          {bundle.expires_on && <TimerBadge availableUntil={bundle.expires_on} />}
        </HStack>
      </VStack>
    );
  }

  return (
    <VStack spacing="12px" alignItems="flex-start" zIndex={1}>
      {hasDiscount && (
        <Box
          bg="success.700"
          color="neutral.white"
          px={2}
          py={1}
          borderRadius="4px"
          fontSize="12px"
          fontWeight="bold"
        >
          {t("components.slideInMenu.bundleDetails.discountOff", {
            discountPercentage,
          })}
        </Box>
      )}
      <PriceDisplay
        price={priceToUse}
        originalPrice={hasDiscount ? bundle.price : undefined}
        hasDiscount={hasDiscount}
      />

      <HStack spacing="12px" width="100%" zIndex={1}>
        <BuyBundleButton
          bundle={bundle}
          isLoggedIn={isLoggedIn}
          setFunnelConfig={setFunnelConfig}
        />
        {bundle.expires_on && <TimerBadge availableUntil={bundle.expires_on} />}
      </HStack>
    </VStack>
  );
}

interface BuyBundleButtonProps {
  bundle: Bundle;
  isLoggedIn: boolean;
  setFunnelConfig?: (config?: FunnelConfigTypes) => void;
}

function BuyBundleButton({ bundle, setFunnelConfig }: BuyBundleButtonProps) {
  const { launchBundlePurchaseFunnel } = useBundlePurchaseFunnel({
    useExternalModal: false,
    setFunnelConfig,
    debug: true,
  });

  const handleBuyClick = useCallback(() => {
    if (!setFunnelConfig) return;

    launchBundlePurchaseFunnel(bundle);
  }, [bundle, setFunnelConfig, launchBundlePurchaseFunnel]);

  return (
    <Button
      variant="primary"
      size="lg"
      fontSize="13px"
      width={{ base: "200px", md: "229px" }}
      height="32px"
      isDisabled={bundle.user_owns_all_items}
      onClick={handleBuyClick}
    >
      {t("components.slideInMenu.bundleDetails.buyBundleNow")}
    </Button>
  );
}
